import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Loader2, ArrowLeft, Trophy, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GameBoard = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [gameResult, setGameResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (gameId) {
      fetchGameState();
    }
  }, [gameId]);

  const fetchGameState = async () => {
    try {
      const response = await axios.get(`${API}/game/${gameId}`);
      setGameState(response.data);
      
      if (response.data.phase === 'completed') {
        const resultResponse = await axios.get(`${API}/game/${gameId}/result`);
        setGameResult(resultResponse.data);
        setShowResult(true);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching game state:', error);
      toast({
        title: 'Error',
        description: 'Failed to load game',
        variant: 'destructive'
      });
      setLoading(false);
    }
  };

  const playCard = async (cardId, laneIndex) => {
    try {
      await axios.post(`${API}/game/${gameId}/play-card`, {
        card_id: cardId,
        lane_index: laneIndex
      });
      
      setSelectedCard(null);
      fetchGameState();
      
      toast({
        title: 'Success',
        description: 'Card played!'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to play card',
        variant: 'destructive'
      });
    }
  };

  const endTurn = async () => {
    try {
      await axios.post(`${API}/game/${gameId}/end-turn`);
      fetchGameState();
      
      toast({
        title: 'Turn Ended',
        description: 'AI is playing...'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to end turn',
        variant: 'destructive'
      });
    }
  };

  const handleCardClick = (card) => {
    if (gameState.phase === 'completed') return;
    setSelectedCard(card);
  };

  const handleLaneClick = (laneIndex) => {
    if (selectedCard && gameState.phase !== 'completed') {
      playCard(selectedCard.id, laneIndex);
    }
  };

  const calculateLanePower = (laneCards, location) => {
    if (!laneCards || laneCards.length === 0) return 0;
    
    let total = laneCards.reduce((sum, pc) => sum + pc.card.power, 0);
    
    if (location.effect_type === 'power_boost') {
      total += (location.effect_value || 0) * laneCards.length;
    } else if (location.effect_type === 'power_multiplier') {
      total *= (location.effect_value || 1);
    }
    
    return total;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900'>
        <Loader2 className='h-12 w-12 animate-spin text-white' />
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className='flex items-center justify-center h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900'>
        <div className='text-white text-center'>
          <h2 className='text-2xl font-bold mb-4'>Game not found</h2>
          <Link to='/campaign'>
            <Button>Back to Campaign</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4' data-testid='game-board'>
      <div className='container mx-auto'>
        <div className='flex items-center justify-between mb-4'>
          <Link to='/campaign'>
            <Button variant='outline' size='sm' data-testid='back-btn'>
              <ArrowLeft className='h-4 w-4 mr-2' /> Back
            </Button>
          </Link>
          
          <div className='flex items-center gap-4 text-white'>
            <div className='flex items-center gap-2'>
              <Trophy className='h-5 w-5 text-yellow-400' />
              <span className='font-bold'>Turn {gameState.current_turn}/6</span>
            </div>
            <div className='flex items-center gap-2'>
              <Zap className='h-5 w-5 text-blue-400' />
              <span className='font-bold'>{gameState.player1_energy} Energy</span>
            </div>
          </div>
          
          <Button 
            onClick={endTurn} 
            disabled={gameState.phase === 'completed'}
            data-testid='end-turn-btn'
          >
            End Turn
          </Button>
        </div>

        <div className='grid grid-cols-3 gap-4 mb-6'>
          {gameState.lanes.map((lane, laneIndex) => {
            const player1Power = calculateLanePower(lane.player1_cards, lane.location);
            const player2Power = calculateLanePower(lane.player2_cards, lane.location);
            const isWinning = player1Power > player2Power;
            const isLosing = player1Power < player2Power;
            
            return (
              <div 
                key={laneIndex}
                className={`bg-white/10 backdrop-blur-sm rounded-lg p-4 border-2 transition-all ${
                  selectedCard 
                    ? 'cursor-pointer hover:border-yellow-400 hover:scale-105' 
                    : isWinning 
                    ? 'border-green-500' 
                    : isLosing 
                    ? 'border-red-500' 
                    : 'border-white/20'
                }`}
                onClick={() => handleLaneClick(laneIndex)}
                data-testid={`lane-${laneIndex}`}
              >
                <div className='text-center mb-4'>
                  <h3 className='text-white font-bold text-lg'>{lane.location.name}</h3>
                  <p className='text-gray-300 text-xs'>{lane.location.effect_description}</p>
                  <div className='flex justify-between mt-2'>
                    <Badge variant='secondary' className='text-xs'>AI: {player2Power}</Badge>
                    <Badge variant='secondary' className='text-xs'>You: {player1Power}</Badge>
                  </div>
                </div>

                <div className='min-h-[80px] mb-2' data-testid={`ai-cards-lane-${laneIndex}`}>
                  <p className='text-gray-400 text-xs mb-1'>AI Cards</p>
                  <div className='flex flex-wrap gap-1'>
                    {lane.player2_cards.map((playedCard, idx) => (
                      <MiniCard key={idx} card={playedCard.card} />
                    ))}
                  </div>
                </div>

                <div className='border-t border-white/30 my-2' />

                <div className='min-h-[80px]' data-testid={`player-cards-lane-${laneIndex}`}>
                  <p className='text-gray-400 text-xs mb-1'>Your Cards</p>
                  <div className='flex flex-wrap gap-1'>
                    {lane.player1_cards.map((playedCard, idx) => (
                      <MiniCard key={idx} card={playedCard.card} />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className='bg-white/10 backdrop-blur-sm rounded-lg p-4 border-2 border-white/20'>
          <h3 className='text-white font-bold mb-3'>Your Hand ({gameState.player1_hand.length} cards)</h3>
          <div className='flex gap-3 overflow-x-auto pb-2' data-testid='player-hand'>
            {gameState.player1_hand.map((card) => (
              <HandCard
                key={card.id}
                card={card}
                selected={selectedCard?.id === card.id}
                onClick={() => handleCardClick(card)}
                canPlay={card.cost <= gameState.player1_energy}
              />
            ))}
          </div>
        </div>
      </div>

      <Dialog open={showResult} onOpenChange={setShowResult}>
        <DialogContent className='bg-gray-900 border-white/20 max-w-2xl' data-testid='game-result-dialog'>
          <DialogHeader>
            <DialogTitle className='text-white text-3xl text-center'>
              {gameResult?.winner === 'player' ? (
                <span className='text-green-400'>Victory!</span>
              ) : gameResult?.winner === 'ai' ? (
                <span className='text-red-400'>Defeat</span>
              ) : (
                <span className='text-yellow-400'>Draw</span>
              )}
            </DialogTitle>
            <DialogDescription className='text-gray-300 text-center text-lg'>
              {gameResult?.winner === 'player' 
                ? 'You won the match!'
                : gameResult?.winner === 'ai'
                ? 'The AI won this round'
                : 'The match ended in a tie'}
            </DialogDescription>
          </DialogHeader>
          
          {gameResult && (
            <div className='space-y-4'>
              <div className='flex justify-center gap-8 text-white'>
                <div className='text-center'>
                  <div className='text-2xl font-bold'>{gameResult.player1_lanes_won}</div>
                  <div className='text-sm text-gray-400'>Your Lanes</div>
                </div>
                <div className='text-center'>
                  <div className='text-2xl font-bold'>{gameResult.player2_lanes_won}</div>
                  <div className='text-sm text-gray-400'>AI Lanes</div>
                </div>
              </div>

              <div className='space-y-2'>
                <h4 className='text-white font-bold'>Lane Results:</h4>
                {gameResult.lane_results.map((lane, idx) => (
                  <div 
                    key={idx}
                    className='bg-white/10 rounded p-3 flex justify-between items-center'
                  >
                    <span className='text-white'>{lane.location_name}</span>
                    <div className='flex gap-4'>
                      <Badge variant={lane.winner === 'player' ? 'default' : 'secondary'}>
                        You: {lane.player1_power}
                      </Badge>
                      <Badge variant={lane.winner === 'ai' ? 'default' : 'secondary'}>
                        AI: {lane.player2_power}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              <div className='flex gap-4'>
                <Button 
                  variant='outline' 
                  className='flex-1'
                  onClick={() => navigate('/campaign')}
                >
                  Back to Campaign
                </Button>
                <Button 
                  className='flex-1'
                  onClick={() => window.location.reload()}
                >
                  Play Again
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const MiniCard = ({ card }) => (
  <div className='bg-gray-800 rounded p-2 min-w-[60px] text-center border border-gray-600'>
    <div className='flex justify-between text-xs mb-1'>
      <Badge className='bg-yellow-600 text-white px-1 py-0 text-xs'>{card.cost}</Badge>
      <Badge className='bg-red-600 text-white px-1 py-0 text-xs'>{card.power}</Badge>
    </div>
    <div className='text-white text-xs font-bold truncate'>{card.name}</div>
  </div>
);

const HandCard = ({ card, selected, onClick, canPlay }) => (
  <div
    onClick={canPlay ? onClick : undefined}
    className={`relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-3 min-w-[150px] border-2 transition-all ${
      selected 
        ? 'border-yellow-400 scale-105 shadow-lg shadow-yellow-400/50' 
        : canPlay
        ? 'border-gray-600 hover:border-blue-500 cursor-pointer hover:scale-105'
        : 'border-gray-700 opacity-50 cursor-not-allowed'
    }`}
    data-testid={`hand-card-${card.id}`}
  >
    <div className='absolute top-2 left-2'>
      <Badge className='bg-yellow-600 text-white font-bold'>{card.cost}</Badge>
    </div>
    <div className='absolute top-2 right-2'>
      <Badge className='bg-red-600 text-white font-bold'>{card.power}</Badge>
    </div>

    <div className='bg-gray-700 rounded h-24 mb-2 mt-6 flex items-center justify-center'>
      {card.image_url ? (
        <img src={card.image_url} alt={card.name} className='w-full h-full object-cover rounded' />
      ) : (
        <div className='text-gray-500 text-3xl font-bold'>{card.name.charAt(0)}</div>
      )}
    </div>

    <h4 className='text-white font-bold text-sm text-center mb-1'>{card.name}</h4>

    {card.ability_type !== 'none' && (
      <Badge className='text-xs w-full justify-center' variant='secondary'>
        {card.ability_type.replace('_', ' ').toUpperCase()}
      </Badge>
    )}

    {!canPlay && (
      <div className='absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center'>
        <span className='text-red-400 font-bold text-xs'>Not enough energy</span>
      </div>
    )}
  </div>
);

export default GameBoard;
